// Searchable select
// -----------------------------------------------------------------------------
// Progressive enhancement for the long `<select>` menus in the app — company
// lists, user lists, risk lists — where scrolling a few hundred native options
// is the only way to find a row.
//
// The native `<select>` is never removed: it stays in the DOM, inside the form,
// keeping its name, id, validation state, Stimulus targets and `change->`
// actions. It is only made invisible, and a button + searchable popup is drawn
// on top of it. Every selection is written back to the native element and a real
// `input` + `change` event is dispatched, so existing listeners keep working and
// form submission is unchanged.
//
// Enhancement is automatic: any select with at least `DEFAULT_MIN_OPTIONS`
// options is upgraded, including ones inserted later by Cocoon/Abyme, by
// StimulusReflex morphs or by hand-written `innerHTML` (see `observeDocument`).
//
// Opt out with `data-searchable="false"` on the select or on any ancestor.
// Opt in below the threshold with `data-searchable="true"`.
// Change the threshold per select with `data-searchable-min="20"`.

const DEFAULT_MIN_OPTIONS = 8;
const MAX_RENDERED_OPTIONS = 300;
const MOBILE_QUERY = "(max-width: 575.98px)";

const FALLBACK_I18N = {
  search: "Rechercher…",
  no_results: "Aucun résultat",
  more_results: "%{count} autres résultats. Affinez votre recherche.",
  none_selected: "Sélectionner",
  clear: "Retirer",
};

let uid = 0;
let cachedI18n = null;

const instances = new Set();

// --- Helpers -----------------------------------------------------------------

// Accent- and case-insensitive, so "leroy" matches "Leroy" and "LEROY" and
// "lerôy" — French names in these lists are routinely typed without accents.
const normalize = (value) =>
  (value == null ? "" : String(value))
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const translations = () => {
  if (cachedI18n) return cachedI18n;

  const meta = document.querySelector('meta[name="searchable-select-i18n"]');
  let parsed = {};

  if (meta) {
    try {
      parsed = JSON.parse(meta.getAttribute("content")) || {};
    } catch (error) {
      parsed = {};
    }
  }

  cachedI18n = Object.assign({}, FALLBACK_I18N, parsed);
  return cachedI18n;
};

const t = (key, interpolations = {}) => {
  const template = translations()[key] || FALLBACK_I18N[key] || key;

  return String(template).replace(/%\{(\w+)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(interpolations, name)
      ? interpolations[name]
      : match
  );
};

const isMobile = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia(MOBILE_QUERY).matches;

// Splits a label on the search tokens so matches can be wrapped in <mark>.
// Built as DOM nodes rather than an HTML string: option labels come from user
// data (company names, emails) and must never be parsed as markup.
const highlight = (label, tokens) => {
  const fragment = document.createDocumentFragment();

  if (!tokens.length) {
    fragment.appendChild(document.createTextNode(label));
    return fragment;
  }

  const haystack = normalize(label);
  const ranges = [];

  tokens.forEach((token) => {
    let from = haystack.indexOf(token);

    while (from !== -1) {
      ranges.push([from, from + token.length]);
      from = haystack.indexOf(token, from + token.length);
    }
  });

  if (!ranges.length) {
    fragment.appendChild(document.createTextNode(label));
    return fragment;
  }

  ranges.sort((a, b) => a[0] - b[0]);

  const merged = [];
  ranges.forEach(([start, end]) => {
    const last = merged[merged.length - 1];
    if (last && start <= last[1]) {
      last[1] = Math.max(last[1], end);
    } else {
      merged.push([start, end]);
    }
  });

  let cursor = 0;
  merged.forEach(([start, end]) => {
    if (start > cursor) {
      fragment.appendChild(document.createTextNode(label.slice(cursor, start)));
    }
    const mark = document.createElement("mark");
    mark.className = "ss__mark";
    mark.textContent = label.slice(start, end);
    fragment.appendChild(mark);
    cursor = end;
  });

  if (cursor < label.length) {
    fragment.appendChild(document.createTextNode(label.slice(cursor)));
  }

  return fragment;
};

// --- The component -----------------------------------------------------------

class SearchableSelect {
  constructor(select) {
    this.select = select;
    this.id = `ss-${++uid}`;
    this.open = false;
    this.options = [];
    this.matches = [];
    this.activeIndex = -1;
    this.query = "";
    this.syncing = false;

    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onWindowChange = this.onWindowChange.bind(this);
    this.onFormReset = this.onFormReset.bind(this);
    this.onNativeFocus = this.onNativeFocus.bind(this);

    this.build();
    this.watchNative();
    this.syncFromNative();
  }

  // --- Markup ---------------------------------------------------------------

  build() {
    const select = this.select;

    this.wrapper = document.createElement("div");
    this.wrapper.className = "ss";
    if (select.multiple) this.wrapper.classList.add("ss--multiple");

    select.parentNode.insertBefore(this.wrapper, select);
    this.wrapper.appendChild(select);

    select.classList.add("ss__native");
    select.setAttribute("tabindex", "-1");
    select.setAttribute("aria-hidden", "true");

    this.trigger = document.createElement("button");
    this.trigger.type = "button";
    this.applyTriggerClasses();
    this.trigger.setAttribute("aria-haspopup", "listbox");
    this.trigger.setAttribute("aria-expanded", "false");
    this.trigger.id = `${this.id}-trigger`;

    const label = this.nativeLabel();
    if (label) {
      if (!label.id) label.id = `${this.id}-label`;
      this.trigger.setAttribute(
        "aria-labelledby",
        `${label.id} ${this.id}-value`
      );
    }

    this.valueEl = document.createElement("span");
    this.valueEl.className = "ss__value";
    this.valueEl.id = `${this.id}-value`;
    this.trigger.appendChild(this.valueEl);

    const caret = document.createElement("span");
    caret.className = "ss__caret";
    caret.setAttribute("aria-hidden", "true");
    this.trigger.appendChild(caret);

    this.wrapper.appendChild(this.trigger);

    this.buildPanel();

    this.trigger.addEventListener("click", (event) => {
      event.preventDefault();
      this.toggle();
    });
    this.trigger.addEventListener("keydown", (event) =>
      this.onTriggerKeydown(event)
    );
    // Clicking the <label> focuses the (invisible) native select; bounce that
    // onto the trigger so the label stays a usable click target.
    select.addEventListener("focus", this.onNativeFocus);

    if (select.form) select.form.addEventListener("reset", this.onFormReset);

    select.dataset.ssEnhanced = "true";
  }

  buildPanel() {
    this.panel = document.createElement("div");
    this.panel.className = "ss__panel";
    this.panel.id = `${this.id}-panel`;
    this.panel.hidden = true;

    const search = document.createElement("div");
    search.className = "ss__search";

    this.input = document.createElement("input");
    this.input.type = "text";
    this.input.className = "ss__search-input";
    this.input.placeholder = t("search");
    this.input.setAttribute("autocomplete", "off");
    this.input.setAttribute("autocorrect", "off");
    this.input.setAttribute("autocapitalize", "off");
    this.input.setAttribute("spellcheck", "false");
    this.input.setAttribute("role", "combobox");
    this.input.setAttribute("aria-autocomplete", "list");
    this.input.setAttribute("aria-expanded", "true");
    this.input.setAttribute("aria-controls", `${this.id}-list`);
    this.input.setAttribute("aria-label", t("search"));

    search.appendChild(this.input);
    this.panel.appendChild(search);

    this.list = document.createElement("ul");
    this.list.className = "ss__list";
    this.list.id = `${this.id}-list`;
    this.list.setAttribute("role", "listbox");
    if (this.select.multiple)
      this.list.setAttribute("aria-multiselectable", "true");
    this.panel.appendChild(this.list);

    this.footer = document.createElement("div");
    this.footer.className = "ss__footer";
    this.footer.hidden = true;
    this.panel.appendChild(this.footer);

    this.trigger.setAttribute("aria-controls", this.panel.id);

    this.input.addEventListener("input", () => {
      this.query = this.input.value;
      this.renderList();
    });
    this.input.addEventListener("keydown", (event) =>
      this.onInputKeydown(event)
    );

    this.list.addEventListener("mousedown", (event) => {
      // Keep focus in the search field so typing can continue after a click.
      event.preventDefault();
    });
    this.list.addEventListener("click", (event) => {
      const row = event.target.closest(".ss__option");
      if (!row || row.getAttribute("aria-disabled") === "true") return;
      this.commit(Number(row.dataset.index));
    });
  }

  // Carry over whatever the view put on the select (`custom-select`,
  // `input-style`, `w-75`, and `is-invalid` once a form comes back with
  // errors) so the trigger keeps the page's own sizing, spacing and states
  // instead of inventing a second look for the same field.
  applyTriggerClasses() {
    const names = ["ss__control"];

    this.select.classList.forEach((name) => {
      if (name !== "ss__native") names.push(name);
    });

    if (!names.includes("custom-select") && !names.includes("form-control")) {
      names.push("custom-select");
    }

    const next = names.join(" ");
    if (this.trigger.className !== next) this.trigger.className = next;
  }

  nativeLabel() {
    if (this.select.id) {
      const explicit = document.querySelector(
        `label[for="${
          CSS && CSS.escape ? CSS.escape(this.select.id) : this.select.id
        }"]`
      );
      if (explicit) return explicit;
    }
    return this.select.closest("label");
  }

  // --- Native <-> component sync --------------------------------------------

  watchNative() {
    // Options are replaced wholesale in several controllers (`pp_form`,
    // `mo_fields`), so the list has to be re-read whenever the DOM changes.
    this.observer = new MutationObserver(() => {
      if (this.syncing) return;
      this.syncFromNative();
      if (this.open) this.renderList();
    });
    this.observer.observe(this.select, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "disabled", "multiple"],
    });

    // `select.value = …` and `select.selectedIndex = …` fire no event, and both
    // are used in the app's inline scripts. Wrap the accessors on this instance
    // so a programmatic change still refreshes what the user sees.
    this.patchAccessors();
  }

  patchAccessors() {
    const proto =
      window.HTMLSelectElement && window.HTMLSelectElement.prototype;
    if (!proto) return;

    this.patched = [];

    ["value", "selectedIndex"].forEach((name) => {
      const descriptor = Object.getOwnPropertyDescriptor(proto, name);
      if (!descriptor || !descriptor.get || !descriptor.set) return;

      try {
        Object.defineProperty(this.select, name, {
          configurable: true,
          enumerable: descriptor.enumerable,
          get() {
            return descriptor.get.call(this);
          },
          set: (incoming) => {
            descriptor.set.call(this.select, incoming);
            if (this.syncing) return;
            this.syncFromNative();
            if (this.open) this.renderList();
          },
        });
        this.patched.push(name);
      } catch (error) {
        // A non-configurable property on an exotic element: fall back to the
        // MutationObserver, which still catches option replacement.
      }
    });
  }

  syncFromNative() {
    this.options = Array.prototype.map.call(
      this.select.options,
      (option, index) => {
        const group =
          option.parentElement && option.parentElement.tagName === "OPTGROUP"
            ? option.parentElement.label
            : null;

        return {
          index,
          label: option.label || option.text || "",
          value: option.value,
          disabled:
            option.disabled ||
            (option.parentElement && option.parentElement.disabled),
          selected: option.selected,
          group,
          haystack: normalize(
            `${option.label || option.text || ""} ${option.value}`
          ),
        };
      }
    );

    this.applyTriggerClasses();
    this.wrapper.classList.toggle("ss--disabled", this.select.disabled);
    this.wrapper.classList.toggle("ss--multiple", this.select.multiple);
    this.trigger.disabled = this.select.disabled;

    this.renderValue();
  }

  renderValue() {
    this.valueEl.textContent = "";
    this.valueEl.classList.remove("ss__value--placeholder");

    const selected = this.options.filter((option) => option.selected);
    const placeholder = () => {
      this.valueEl.classList.add("ss__value--placeholder");
      this.valueEl.textContent = t("none_selected");
    };

    if (!selected.length) {
      placeholder();
      return;
    }

    if (!this.select.multiple) {
      // A blank first option (`include_blank`) reads as "nothing chosen yet".
      if (selected[0].label.trim() === "") placeholder();
      else this.valueEl.textContent = selected[0].label;
      return;
    }

    selected.forEach((option) => {
      const chip = document.createElement("span");
      chip.className = "ss__chip";

      const text = document.createElement("span");
      text.className = "ss__chip-label";
      text.textContent = option.label;
      chip.appendChild(text);

      const remove = document.createElement("span");
      remove.className = "ss__chip-remove";
      remove.setAttribute("role", "button");
      remove.setAttribute("tabindex", "-1");
      remove.setAttribute("aria-label", `${t("clear")} ${option.label}`);
      remove.textContent = "\u00d7";
      remove.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.deselect(option.index);
      });
      chip.appendChild(remove);

      this.valueEl.appendChild(chip);
    });
  }

  // --- Filtering and list rendering -----------------------------------------

  tokens() {
    return normalize(this.query).split(/\s+/).filter(Boolean);
  }

  filtered() {
    const tokens = this.tokens();
    if (!tokens.length) return this.options.slice();

    const scored = [];

    this.options.forEach((option) => {
      if (!tokens.every((token) => option.haystack.includes(token))) return;

      const first = tokens[0];
      let rank = 2;
      if (option.haystack.startsWith(first)) rank = 0;
      else if (
        new RegExp(`\\b${first.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`).test(
          option.haystack
        )
      )
        rank = 1;

      scored.push({ option, rank, position: scored.length });
    });

    // Best matches first, original order preserved within a rank.
    return scored
      .sort((a, b) => a.rank - b.rank || a.position - b.position)
      .map((entry) => entry.option);
  }

  renderList() {
    this.matches = this.filtered();
    this.list.textContent = "";

    const tokens = this.tokens();
    const shown = this.matches.slice(0, MAX_RENDERED_OPTIONS);
    let lastGroup = null;

    shown.forEach((option) => {
      if (option.group && option.group !== lastGroup) {
        const header = document.createElement("li");
        header.className = "ss__group";
        header.setAttribute("role", "presentation");
        header.textContent = option.group;
        this.list.appendChild(header);
      }
      lastGroup = option.group;

      const row = document.createElement("li");
      row.className = "ss__option";
      row.id = `${this.id}-option-${option.index}`;
      row.dataset.index = String(option.index);
      row.setAttribute("role", "option");
      row.setAttribute("aria-selected", option.selected ? "true" : "false");
      if (option.disabled) row.setAttribute("aria-disabled", "true");

      const text = document.createElement("span");
      text.className = "ss__option-label";
      const displayed =
        option.label.trim() === "" ? t("none_selected") : option.label;
      text.appendChild(highlight(displayed, tokens));
      row.appendChild(text);

      this.list.appendChild(row);
    });

    if (!shown.length) {
      const empty = document.createElement("li");
      empty.className = "ss__empty";
      empty.setAttribute("role", "presentation");
      empty.textContent = t("no_results");
      this.list.appendChild(empty);
    }

    const hidden = this.matches.length - shown.length;
    this.footer.hidden = hidden <= 0;
    if (hidden > 0)
      this.footer.textContent = t("more_results", { count: hidden });

    const firstSelected = shown.findIndex(
      (option) => option.selected && !option.disabled
    );
    const firstEnabled = shown.findIndex((option) => !option.disabled);
    this.setActive(firstSelected !== -1 ? firstSelected : firstEnabled);
  }

  setActive(position) {
    const rows = this.list.querySelectorAll(".ss__option");

    rows.forEach((row) => row.classList.remove("ss__option--active"));
    this.activeIndex = position;

    if (position < 0 || position >= rows.length) {
      this.input.removeAttribute("aria-activedescendant");
      return;
    }

    const row = rows[position];
    row.classList.add("ss__option--active");
    this.input.setAttribute("aria-activedescendant", row.id);

    const rowTop = row.offsetTop;
    const rowBottom = rowTop + row.offsetHeight;
    if (rowTop < this.list.scrollTop) {
      this.list.scrollTop = rowTop;
    } else if (rowBottom > this.list.scrollTop + this.list.clientHeight) {
      this.list.scrollTop = rowBottom - this.list.clientHeight;
    }
  }

  moveActive(delta) {
    const rows = this.list.querySelectorAll(".ss__option");
    if (!rows.length) return;

    let next = this.activeIndex;
    for (let step = 0; step < rows.length; step += 1) {
      next = (next + delta + rows.length) % rows.length;
      if (rows[next].getAttribute("aria-disabled") !== "true") break;
    }
    this.setActive(next);
  }

  // --- Selection ------------------------------------------------------------

  commit(index) {
    const option = this.select.options[index];
    if (!option || option.disabled) return;

    this.syncing = true;
    if (this.select.multiple) {
      option.selected = !option.selected;
    } else {
      this.select.selectedIndex = index;
    }
    this.syncing = false;

    this.syncFromNative();
    this.notify();

    if (this.select.multiple) {
      this.renderList();
      this.input.focus();
    } else {
      this.close({ focus: true });
    }
  }

  deselect(index) {
    const option = this.select.options[index];
    if (!option) return;

    this.syncing = true;
    option.selected = false;
    this.syncing = false;

    this.syncFromNative();
    this.notify();
    if (this.open) this.renderList();
  }

  // Real, bubbling events so `change->controller#action`, jQuery handlers and
  // anything else already listening on the select still fire.
  notify() {
    this.select.dispatchEvent(new Event("input", { bubbles: true }));
    this.select.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // --- Open / close ---------------------------------------------------------

  toggle() {
    if (this.open) this.close({ focus: true });
    else this.show();
  }

  show(seed = "") {
    if (this.open || this.select.disabled) return;

    instances.forEach((instance) => {
      if (instance !== this && instance.open) instance.close();
    });

    this.open = true;
    this.query = seed;
    this.input.value = seed;

    document.body.appendChild(this.panel);
    this.panel.hidden = false;
    this.wrapper.classList.add("ss--open");
    this.trigger.setAttribute("aria-expanded", "true");

    this.renderList();
    this.position();

    document.addEventListener("mousedown", this.onDocumentPointerDown, true);
    document.addEventListener("touchstart", this.onDocumentPointerDown, true);
    window.addEventListener("resize", this.onWindowChange);
    window.addEventListener("scroll", this.onWindowChange, true);

    this.input.focus();
    if (seed) this.input.setSelectionRange(seed.length, seed.length);
  }

  close({ focus = false } = {}) {
    if (!this.open) return;

    this.open = false;
    this.panel.hidden = true;
    this.panel.classList.remove("ss__panel--sheet");
    if (this.panel.parentNode) this.panel.parentNode.removeChild(this.panel);
    if (this.backdrop && this.backdrop.parentNode) {
      this.backdrop.parentNode.removeChild(this.backdrop);
    }
    this.wrapper.classList.remove("ss--open");
    this.trigger.setAttribute("aria-expanded", "false");

    document.removeEventListener("mousedown", this.onDocumentPointerDown, true);
    document.removeEventListener(
      "touchstart",
      this.onDocumentPointerDown,
      true
    );
    window.removeEventListener("resize", this.onWindowChange);
    window.removeEventListener("scroll", this.onWindowChange, true);

    if (focus) this.trigger.focus();
  }

  // The panel is rendered in <body> rather than next to the field: several of
  // these selects sit inside modals and scrolling cards that would otherwise
  // clip the dropdown.
  position() {
    if (isMobile()) {
      this.panel.classList.add("ss__panel--sheet");
      this.panel.style.left = "";
      this.panel.style.top = "";
      this.panel.style.width = "";
      this.panel.style.maxHeight = "";

      if (!this.backdrop) {
        this.backdrop = document.createElement("div");
        this.backdrop.className = "ss__backdrop";
      }
      document.body.insertBefore(this.backdrop, this.panel);
      return;
    }

    this.panel.classList.remove("ss__panel--sheet");
    if (this.backdrop && this.backdrop.parentNode) {
      this.backdrop.parentNode.removeChild(this.backdrop);
    }

    const rect = this.wrapper.getBoundingClientRect();
    const margin = 8;
    const width = Math.max(rect.width, 240);
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const below = spaceBelow >= 220 || spaceBelow >= spaceAbove;
    const available = Math.min(360, below ? spaceBelow : spaceAbove);

    this.panel.style.width = `${width}px`;
    this.panel.style.maxHeight = `${Math.max(available, 160)}px`;
    this.panel.style.left = `${Math.min(
      Math.max(rect.left, margin),
      Math.max(window.innerWidth - width - margin, margin)
    )}px`;

    if (below) {
      this.panel.style.top = `${rect.bottom + 4}px`;
    } else {
      this.panel.style.top = `${Math.max(
        rect.top - 4 - Math.max(available, 160),
        margin
      )}px`;
    }
  }

  // --- Event handlers -------------------------------------------------------

  onTriggerKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
      case "Enter":
      case " ":
      case "Spacebar":
        event.preventDefault();
        this.show();
        break;
      default:
        // Start searching straight from the closed field, like a native select
        // does for first-letter jumps — only richer.
        if (
          event.key.length === 1 &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.altKey
        ) {
          event.preventDefault();
          this.show(event.key);
        }
    }
  }

  onInputKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.moveActive(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.moveActive(-1);
        break;
      case "Home":
        event.preventDefault();
        this.setActive(0);
        break;
      case "End":
        event.preventDefault();
        this.setActive(this.list.querySelectorAll(".ss__option").length - 1);
        break;
      case "Enter": {
        event.preventDefault();
        const row = this.list.querySelectorAll(".ss__option")[this.activeIndex];
        if (row && row.getAttribute("aria-disabled") !== "true") {
          this.commit(Number(row.dataset.index));
        }
        break;
      }
      case "Escape":
        event.preventDefault();
        this.close({ focus: true });
        break;
      case "Tab":
        this.close();
        break;
      default:
        break;
    }
  }

  onDocumentPointerDown(event) {
    if (
      this.panel.contains(event.target) ||
      this.wrapper.contains(event.target)
    )
      return;
    this.close();
  }

  onWindowChange() {
    if (!this.open) return;
    this.position();
  }

  onFormReset() {
    window.setTimeout(() => this.syncFromNative(), 0);
  }

  onNativeFocus() {
    if (this.open) return;
    this.trigger.focus();
  }

  // --- Teardown -------------------------------------------------------------

  destroy() {
    this.close();
    if (this.observer) this.observer.disconnect();
    (this.patched || []).forEach((name) => {
      delete this.select[name];
    });
    this.select.removeEventListener("focus", this.onNativeFocus);
    if (this.select.form)
      this.select.form.removeEventListener("reset", this.onFormReset);
    delete this.select.dataset.ssEnhanced;
    this.select.classList.remove("ss__native");
    this.select.removeAttribute("aria-hidden");
    this.select.removeAttribute("tabindex");
    instances.delete(this);
  }
}

// --- Auto-enhancement --------------------------------------------------------

const threshold = (select) => {
  const declared = parseInt(select.dataset.searchableMin, 10);
  return Number.isNaN(declared) ? DEFAULT_MIN_OPTIONS : declared;
};

const shouldEnhance = (select) => {
  if (select.dataset.ssEnhanced) return false;
  if (select.closest('[data-searchable="false"]')) return false;
  if (select.dataset.searchable === "true") return true;
  return select.options.length >= threshold(select);
};

export const enhance = (select) => {
  if (!shouldEnhance(select)) return null;

  const instance = new SearchableSelect(select);
  instances.add(instance);
  return instance;
};

export const enhanceAll = (root = document) => {
  root.querySelectorAll("select").forEach(enhance);
};

const prune = () => {
  instances.forEach((instance) => {
    if (!document.contains(instance.select)) instance.destroy();
  });
};

const observeDocument = () => {
  let scheduled = false;

  const scan = () => {
    scheduled = false;
    prune();
    enhanceAll();
  };

  const observer = new MutationObserver((mutations) => {
    if (scheduled) return;
    // Ignore the mutations we cause ourselves while building a wrapper.
    const relevant = mutations.some(
      (mutation) =>
        !(mutation.target.closest && mutation.target.closest(".ss__panel"))
    );
    if (!relevant) return;

    scheduled = true;
    window.requestAnimationFrame(scan);
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
};

const searchableSelects = () => {
  if (!("MutationObserver" in window) || !document.body) return;

  enhanceAll();
  observeDocument();
};

export default searchableSelects;
